import { useEffect, useState } from 'react';
import { useEmployee } from './EmployeeContext';
import { useModal } from '../../components/ModalProvider';
import { useEnsureOnboarding } from './onboarding/useEnsureOnboarding';
import { employeeApi } from '../../lib/api';
import { RECV_TYPES } from '../../lib/constants';
import { nowISO } from '../../lib/format';
import { capturePhoto, deviceMeta, tryGetIP, getCurrentPosition, geoPreCheckMessage, geoErrorMessage, urlToDataUrl } from '../../lib/photo';
import { generateReceiptPDF } from '../../lib/pdf';
import { ErrorModalBody } from '../../components/ErrorModalBody';
import { fmtDateBR } from '../../lib/format';
import type { RecebimentoTipo } from '../../lib/types';

export function RecebimentosTab() {
  const { employee, vtValorPadrao, vtDataPadrao } = useEmployee();
  const { showModal, closeModal } = useModal();
  const runGated = useEnsureOnboarding();
  const [recs, setRecs] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  const tipos: RecebimentoTipo[] = ['vt', 'uniforme', ...(employee.recebeEpi ? (['epi'] as RecebimentoTipo[]) : [])];

  async function reload() {
    const entries = await Promise.all(tipos.map(async (t) => [t, (await employeeApi.getRecebimento(t)).record] as const));
    setRecs(Object.fromEntries(entries));
  }
  useEffect(() => { setLoading(true); reload().finally(() => setLoading(false)); }, []);

  function openModal(tipo: RecebimentoTipo) {
    runGated(() => {
      const info = RECV_TYPES[tipo];
      const vtPadrao = tipo === 'vt' && vtValorPadrao && vtDataPadrao;
      const descricaoPadrao = vtPadrao ? `Vale-transporte — R$ ${vtValorPadrao}, no dia ${fmtDateBR(vtDataPadrao!)}` : '';

      const Body = () => {
        const [desc, setDesc] = useState(descricaoPadrao);
        const [err, setErr] = useState('');
        return (
          <>
            <h2>Registrar recebimento — {info.label}</h2>
            <p className="hint">Confirme exatamente o que você recebeu hoje.</p>
            {vtPadrao ? (
              <div className="banner">{descricaoPadrao}</div>
            ) : (
              <>
                <label>Descrição do que recebeu</label>
                <input type="text" placeholder={info.ph} value={desc} onChange={(e) => setDesc(e.target.value)} />
              </>
            )}
            {err && <div className="err">{err}</div>}
            <div className="row" style={{ marginTop: 14 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={closeModal}>Cancelar</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => {
                if (!desc.trim()) { setErr('Descreva o que você recebeu.'); return; }
                startFlow(tipo, desc.trim());
              }}>Confirmar recebimento</button>
            </div>
          </>
        );
      };
      showModal(<Body />);
    });
  }

  function startFlow(tipo: RecebimentoTipo, desc: string) {
    showModal(<div style={{ textAlign: 'center', padding: '20px 0' }}>
      <p className="muted">Vamos confirmar sua localização e depois tirar uma selfie — ambas obrigatórias.</p>
      <button className="btn btn-primary btn-block" onClick={() => geoStep(tipo, desc)}>Continuar</button>
      <button className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={closeModal}>Cancelar</button>
    </div>);
  }

  function geoStep(tipo: RecebimentoTipo, desc: string) {
    const geoMsg = geoPreCheckMessage();
    if (geoMsg) {
      showModal(<><h2 style={{ color: 'var(--coral)' }}>Localização indisponível</h2><p className="muted">{geoMsg}</p><button className="btn btn-ghost btn-block" onClick={closeModal}>Entendi</button></>);
      return;
    }
    showModal(<div style={{ textAlign: 'center', padding: '20px 0' }}><p className="muted">Obtendo localização…</p></div>);
    getCurrentPosition().then((pos) => {
      const lat = pos.coords.latitude, lng = pos.coords.longitude, accuracy = Math.round(pos.coords.accuracy);
      showModal(<div style={{ textAlign: 'center', padding: '16px 0' }}>
        <p className="muted">Localização confirmada. Agora tire uma selfie para confirmar sua identidade — é obrigatória para concluir o registro.</p>
        <button className="btn btn-primary btn-block" onClick={() => finishPhoto(tipo, desc, lat, lng, accuracy)}>Tirar selfie</button>
        <button className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={closeModal}>Cancelar</button>
      </div>);
    }).catch((err) => {
      showModal(<>
        <h2 style={{ color: 'var(--coral)' }}>Não foi possível obter a localização</h2>
        <p className="muted">{geoErrorMessage(err)}</p>
        <button className="btn btn-primary btn-block" onClick={() => geoStep(tipo, desc)}>Tentar novamente</button>
        <button className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={closeModal}>Fechar</button>
      </>);
    });
  }

  async function finishPhoto(tipo: RecebimentoTipo, desc: string, lat: number, lng: number, accuracy: number) {
    try {
      showModal(<div style={{ textAlign: 'center', padding: '20px 0' }}><p className="muted">Abrindo câmera…</p></div>);
      const meta = [employee.nome, `${RECV_TYPES[tipo].label} — ${new Date().toLocaleString('pt-BR')}`, `Lat ${lat.toFixed(5)}, Lng ${lng.toFixed(5)}`];
      const photo = await capturePhoto('user', meta);
      if (!photo) {
        showModal(<><h2 style={{ color: 'var(--coral)' }}>Foto obrigatória</h2><p className="muted">Não foi possível confirmar sem a selfie. Tente novamente.</p><button className="btn btn-ghost btn-block" onClick={closeModal}>Fechar</button></>);
        return;
      }
      const ip = await tryGetIP();
      const extra = tipo === 'vt' ? { valor: vtValorPadrao, dataRecebimento: vtDataPadrao } : {};
      await employeeApi.confirmRecebimento({ tipo, descricao: desc, dataHora: nowISO(), lat, lng, accuracy, foto: photo, ip, device: deviceMeta(), ...extra });
      closeModal();
      await reload();
    } catch (err) {
      showModal(<ErrorModalBody error={err} onClose={closeModal} onRetry={() => finishPhoto(tipo, desc, lat, lng, accuracy)} />);
    }
  }

  async function downloadPDF(tipo: RecebimentoTipo) {
    const r = recs[tipo];
    if (!r) return;
    const foto = r.fotoUrl ? await urlToDataUrl(r.fotoUrl) : null;
    generateReceiptPDF(`Comprovante de Recebimento — ${RECV_TYPES[tipo].label}`, employee, {
      Descrição: r.descricao,
      'Confirmado por': r.confirmadoPor,
      'Data/Hora': new Date(r.dataHora).toLocaleString('pt-BR'),
      Localização: r.lat ? `${r.lat}, ${r.lng}` : '—',
      'Verificação em cadeia': (r.hashAnterior || '').slice(0, 20) + '…',
    }, foto, r.hash);
  }

  if (loading) return <div className="card"><p className="muted">Carregando…</p></div>;

  return (
    <>
      <div className="card">
        <h2>Recebimentos</h2>
        <p className="hint">Vale-transporte{employee.recebeEpi ? ', uniforme e EPI' : ' e uniforme'} são confirmados uma única vez, com localização e selfie.</p>
      </div>
      {tipos.map((t) => {
        const info = RECV_TYPES[t];
        const r = recs[t];
        return (
          <div className="recv-card" key={t}>
            <div className="ttl">{info.label}</div>
            {r ? (
              <>
                <p className="muted" style={{ margin: '8px 0 0' }}>"{r.descricao}" — recebido em {new Date(r.dataHora).toLocaleString('pt-BR')}</p>
                <div className="pill pill-ok" style={{ marginTop: 8 }}>✓ confirmado</div>
                <button className="btn btn-ghost btn-sm btn-block" style={{ marginTop: 10 }} onClick={() => downloadPDF(t)}>Baixar comprovante</button>
              </>
            ) : (
              <>
                <p className="hint" style={{ margin: '8px 0 12px' }}>Registro único — confirme assim que receber.</p>
                <button className="btn btn-primary btn-block" onClick={() => openModal(t)}>Registrar recebimento</button>
              </>
            )}
          </div>
        );
      })}
    </>
  );
}
